import useQuery from '@/api/queries/useQuery';
import { usePartnerScoreGlobalFilter } from '@/components/partner';
import { FilterLimitPresets } from '@/models/filter/filterDefaultLimits';
import { CubeQuery } from '@/types/cube';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { PartnerScoreCacheKeys } from './cacheKeys';

import { toNumberOrNull } from '@/utils/cube';

const PARTNER_SCORE_CUBE_NAME = 'partnerscore' as const;
const COMPETITOR_CUBE_NAME = 'competitor_partner_score';

type UseGetPartnerScoreYearQuartersParams = {
  CUBE_NAME?: typeof PARTNER_SCORE_CUBE_NAME | typeof COMPETITOR_CUBE_NAME;
};

type UseGetPartnerScoreYearQuartersResponse = {
  year: number;
  quarter: number;
}[];

function useGetPartnerScoreYearQuarters({
  CUBE_NAME = PARTNER_SCORE_CUBE_NAME,
}: UseGetPartnerScoreYearQuartersParams = {}): UseQueryResult<UseGetPartnerScoreYearQuartersResponse, AxiosError> {
  const { countries, globalFiltersWithoutHcpCountryCode } = usePartnerScoreGlobalFilter();

  // The available years/quarters must reflect the same HCO Characteristics / global
  // filters the widgets apply. Otherwise this returns a superset of years and the
  // computed latest/default year and previous-period no longer match the filtered
  // widget data, blanking the Data Availability, Engagement Qualities and CSAT widgets.
  const query: CubeQuery = {
    dimensions: [`${CUBE_NAME}.quarter`, `${CUBE_NAME}.year`, `${CUBE_NAME}.country_code`],
    filters: [
      {
        member: `${CUBE_NAME}.country_code`,
        operator: 'equals',
        values: countries,
      },
      globalFiltersWithoutHcpCountryCode,
    ],
    order: { [`${CUBE_NAME}.year`]: 'desc', [`${CUBE_NAME}.quarter`]: 'asc' },
    limit: FilterLimitPresets.LIMIT_5K,
  };

  const isEnabled = !!countries.length;

  return useQuery<CubeRawRow[], AxiosError, UseGetPartnerScoreYearQuartersResponse>({
    payload: query,
    options: {
      queryKey: [...PartnerScoreCacheKeys.partnerScoreFilter(query)],
      select: (data): UseGetPartnerScoreYearQuartersResponse => {
        if (!data.length) return [];

        const result = data.map(record => ({
          year: toNumberOrNull(record[`${CUBE_NAME}.year`]) ?? 0,
          quarter: toNumberOrNull(record[`${CUBE_NAME}.quarter`]) ?? 0,
        }));

        return result;
      },
      enabled: isEnabled,
    },
  });
}

export default useGetPartnerScoreYearQuarters;
